import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Minus, Edit, Upload, Eye, FileText, Loader2, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { StatusIcon } from '@components/common';
import clsx from 'clsx';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import {
  AssessmentItem,
  AssessmentStatus,
  ItemEvaluation,
  Evidence,
  EvidenceForReview,
  EvidenceStatus,
  ROLES,
} from '@app-types';
import { getProcessTypeLabel } from '@utils/referenceCatalog';
import { hasRole, canUserUploadEvidence, getDraftEvidencePermissions } from '@utils/permissionUtils';
import { processTypeToVariant } from '@utils/badgeMappers';
import { useAuth } from '@contexts/AuthContext';
import ProcessEvaluationModal from './ProcessEvaluationModal';
import EvidenceReviewModal from '../evidence/EvidenceReviewModal';
import DraftEvidenceActions from '../evidence/DraftEvidenceActions';
import { evidenceService } from '@services';
import {
  canShowUploadEvidenceButton,
  DRAFT_EVIDENCE_LABEL,
  findDraftEvidence,
  getProcessEvidenceLabel
} from '@utils/evidenceVisibility';
import { showToast } from '@utils/toast';
import styles from './ProcessItem.module.css';

interface ProcessItemProps {
  process: AssessmentItem;
  canUpdate?: boolean;
  onUpdate?: () => void;
  contractNumber?: string;
  supplierName?: string;
  assessmentStatus?: AssessmentStatus;
  assessorId?: number | null;
}

const PRE_SUBMISSION_STATUSES: AssessmentStatus[] = ['Pending', 'Scheduled', 'Rescheduled', 'Not Scheduled'];

interface EvidenceLabelStyle {
  Icon: LucideIcon;
  className: string;
}

// Draft is absent on purpose: it renders its own label (getProcessEvidenceLabel returns null for it).
const EVIDENCE_LABEL_STYLES: Record<Exclude<EvidenceStatus, 'draft'>, EvidenceLabelStyle> = {
  none: { Icon: Upload, className: styles.evidenceLabelWarning },
  pending: { Icon: Clock, className: styles.evidenceLabelInfo },
  approved: { Icon: CheckCircle, className: styles.evidenceLabelSuccess },
  rejected: { Icon: AlertCircle, className: styles.evidenceLabelWarning },
  mixed: { Icon: FileText, className: styles.evidenceLabelNeutral }
};

const getEvaluationIcon = (evaluation: ItemEvaluation) => {
  switch (evaluation) {
    case 'Implemented':
      return <StatusIcon status="success" size={16} />;
    case 'Implemented (Needs Evidence)':
      return <FileText size={16} style={{ color: 'var(--color-warning-600)' }} />;
    case 'Gap':
      return <StatusIcon status="error" size={16} />;
    case 'Not Applicable':
      return <Minus size={16} style={{ color: 'var(--color-gray-500)' }} />;
    case 'Not Assessed':
    default:
      return <StatusIcon status="warning" size={16} />;
  }
};

const getEvaluationClass = (evaluation: ItemEvaluation) => {
  switch (evaluation) {
    case 'Implemented':
      return styles.processImplemented;
    case 'Implemented (Needs Evidence)':
      return styles.processImplementedNeedsEvidence;
    case 'Gap':
      return styles.processGap;
    case 'Not Applicable':
      return styles.processNa;
    case 'Not Assessed':
    default:
      return styles.processPending;
  }
};

const getPriorityClass = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return styles.priorityHigh;
    case 'medium':
      return styles.priorityMedium;
    case 'low':
      return styles.priorityLow;
    default:
      return styles.priorityMedium;
  }
};

const ProcessItem: React.FC<ProcessItemProps> = ({ process, canUpdate = false, onUpdate, contractNumber, supplierName, assessmentStatus, assessorId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);

  // Evidence review modal state - exactly like EvidenceReviewDashboard
  const [evidenceToReview, setEvidenceToReview] = useState<EvidenceForReview | null>(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [loadingEvidenceAction, setLoadingEvidenceAction] = useState<'review' | 'view' | null>(null);

  const evidenceStatus = process.evidence_status ?? 'none';
  const pendingCount = process.pending_count ?? 0;
  const approvedCount = process.approved_count ?? 0;

  const handleProcessClick = () => {
    if (canUpdate) {
      setShowEvaluationModal(true);
    }
  };

  const handleModalClose = () => {
    setShowEvaluationModal(false);
  };

  const handleEvaluationSaved = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  // Evidence review handlers - exactly like EvidenceReviewDashboard
  const handleReviewEvidence = (evidenceItem: EvidenceForReview) => {
    setEvidenceToReview(evidenceItem);
    setShowEvidenceModal(true);
  };

  // Handle modal close with refresh
  const handleEvidenceModalClose = () => {
    setEvidenceToReview(null);
    setShowEvidenceModal(false);
    // Refresh assessment when evidence review is complete
    if (onUpdate) {
      onUpdate();
    }
  };

  const mapToEvidenceForReview = (source: Evidence): EvidenceForReview => ({
    id: source.id,
    evidence_name: source.evidence_name,
    evidence_description: source.evidence_description,
    evidence_type: source.evidence_type,
    status: source.status,
    notes: source.notes || '',
    uploaded_by_id: source.uploaded_by_id,
    uploaded_by_name: source.uploaded_by_name || 'Unknown',
    submitted_by_id: source.submitted_by_id,
    submitted_by_name: source.submitted_by_name,
    submitted_at: source.submitted_at,
    created_at: source.created_at,
    assessment_item_id: process.id,
    process_name: process.process_name,
    process_priority: process.process_priority,
    domain_name: process.domain_name,
    assessment_id: process.assessment_id,
    assessment_name: `Assessment ${process.assessment_id}`,
    contract_number: contractNumber || '',
    supplier_name: supplierName || '',
    process_evaluation: process.evaluation,
    files: source.files || []
  });

  const handleOpenEvidence = (source: Evidence | undefined) => {
    if (!source) {
      return;
    }
    handleReviewEvidence(mapToEvidenceForReview(source));
  };

  const openEvidence = async (kind: 'pending' | 'approved') => {
    setLoadingEvidenceAction(kind === 'pending' ? 'review' : 'view');
    try {
      const data = await evidenceService.getEvidenceForItem(process.id);
      const list = data.evidence || [];
      const source =
        kind === 'pending'
          ? list.find((item) => item.status === 'pending')
          : [...list.filter((item) => item.status === 'approved')].sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0];
      if (!source) {
        showToast.error('Evidence could not be loaded. Please try again.');
        return;
      }
      handleOpenEvidence(source);
    } catch {
      showToast.error('Evidence could not be loaded. Please try again.');
    } finally {
      setLoadingEvidenceAction(null);
    }
  };

  // Evidence label for Assessors on Gap / Implemented (Needs Evidence) items. Derived from the item's
  // evidence status (same labels as Evidence Management), so "Evidence Needed" only shows when nothing was uploaded.
  const evidenceLabel =
    hasRole(user, ROLES.ASSESSOR) &&
    (process.evaluation === 'Gap' || process.evaluation === 'Implemented (Needs Evidence)')
      ? getProcessEvidenceLabel(evidenceStatus)
      : null;
  const evidenceLabelStyle = evidenceStatus === 'draft' ? undefined : EVIDENCE_LABEL_STYLES[evidenceStatus];

  // Show Review Evidence only for the assigned assessor, after submission, when this item has pending evidence.
  const isAssignedAssessor = hasRole(user, ROLES.ASSESSOR) && user?.id === assessorId;
  const isAssessmentSubmitted =
    !!assessmentStatus && !PRE_SUBMISSION_STATUSES.includes(assessmentStatus);
  const showReviewButton =
    isAssignedAssessor &&
    (process.evaluation === 'Gap' || process.evaluation === 'Implemented (Needs Evidence)') &&
    isAssessmentSubmitted &&
    pendingCount > 0;

  // View Evidence for approved files when Review Evidence is not shown (Bug #1137)
  const showViewButton = !showReviewButton && approvedCount > 0;

  const needsEvidence = process.evaluation === 'Gap' || process.evaluation === 'Implemented (Needs Evidence)';

  // Upload for Contract Role users and the assigned Assessor (ADO-1148: the Assessor's upload is a draft).
  // Aligned with Evidence Management page: show only when no evidence exists or all evidence is rejected.
  // Hide when a draft exists, or any evidence is pending (under review) or approved.
  const showUploadButton =
    canUserUploadEvidence(user, assessorId, assessmentStatus) && needsEvidence && canShowUploadEvidenceButton(evidenceStatus);

  const isDraft = evidenceStatus === 'draft';
  // Draft rights depend on who uploaded it, which only the evidence record carries; load it for draft rows only.
  const { data: itemEvidence } = useQuery({
    queryKey: ['assessment-item-evidence', process.id],
    queryFn: () => evidenceService.getEvidenceForItem(process.id),
    enabled: isDraft
  });
  const draftEvidence = isDraft ? findDraftEvidence(itemEvidence?.evidence ?? []) : undefined;
  const draftPermissions = getDraftEvidencePermissions(user, draftEvidence?.uploaded_by_id);

  return (
    <>
      <button
        type="button"
        id={`process-item-${process.id}`}
        className={clsx(styles.processItem, {
          [styles.clickable]: canUpdate
        })}
        onClick={handleProcessClick}
        data-test-id="process-item"
      >
        <div className={styles.processHeader}>
          <div className={styles.processInfo}>
            <div className={styles.processTitle}>
              <div className={styles.processIcon}>{getEvaluationIcon(process.evaluation)}</div>
              <div className={styles.processName}>
                {process.process_name}
                <span className={clsx(styles.processPriority, getPriorityClass(process.process_priority))}>
                  {process.process_priority}
                </span>
                <Badge
                  variant={processTypeToVariant(process.process_type)}
                  title={getProcessTypeLabel(process.process_type)}
                  ariaLabel={getProcessTypeLabel(process.process_type)}
                  dataTestId="process-type-badge"
                >
                  {process.process_type}
                </Badge>
              </div>
            </div>
            {process.process_description && (
              <div className={styles.processDescription}>{process.process_description}</div>
            )}
            {evidenceLabel && evidenceLabelStyle && (
              <div
                className={clsx(styles.evidenceStatusLabel, evidenceLabelStyle.className)}
                data-test-id="process-evidence-label"
              >
                <evidenceLabelStyle.Icon size={14} />
                <span>{evidenceLabel}</span>
              </div>
            )}
            {isDraft && (
              <div className={styles.draftEvidenceLabel} data-test-id="draft-evidence-label">
                <FileText size={14} />
                <span>{DRAFT_EVIDENCE_LABEL}</span>
              </div>
            )}
          </div>

          <div className={styles.processStatus}>
            <span className={clsx(styles.evaluationBadge, getEvaluationClass(process.evaluation))}>
              {process.evaluation}
            </span>
            {canUpdate && (
              <div className={styles.editIndicator}>
                <Edit size={16} />
                <span>Click to evaluate</span>
              </div>
            )}
            {/* Review Evidence Button - exactly like EvidenceReviewDashboard */}
            {showReviewButton && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={loadingEvidenceAction !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    void openEvidence('pending');
                  }}
                >
                  {loadingEvidenceAction === 'review' ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  Review Evidence
                </Button>
            )}
            {showViewButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={loadingEvidenceAction !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    void openEvidence('approved');
                  }}
                >
                  {loadingEvidenceAction === 'view' ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  View Evidence
                </Button>
            )}
            {/* Upload Evidence Button for Contract Role */}
            {showUploadButton && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/evidence/upload/${process.id}`, {
                      state: { returnTo: `${location.pathname}${location.search}` }
                    });
                  }}
                >
                  <Upload size={14} />
                  {evidenceStatus === 'none' ? 'Upload Evidence' : 'Resubmit Evidence'}
                </Button>
            )}
            {isDraft && (
              <DraftEvidenceActions
                assessmentItemId={process.id}
                processName={process.process_name}
                permissions={draftPermissions}
                onChanged={onUpdate}
              />
            )}
          </div>
        </div>

        {process.notes && (
          <div className={styles.processNotes}>
            <strong>Notes:</strong> {process.notes}
          </div>
        )}
      </button>

      {/* Evaluation Modal */}
      {showEvaluationModal && (
        <ProcessEvaluationModal item={process} onClose={handleModalClose} onSaved={handleEvaluationSaved} />
      )}

      {/* Evidence Review Modal - exactly like EvidenceReviewDashboard */}
      {showEvidenceModal && evidenceToReview && (
        <EvidenceReviewModal
          isOpen={showEvidenceModal}
          evidence={evidenceToReview}
          onClose={handleEvidenceModalClose}
          hideAssessmentLink={true}
        />
      )}
    </>
  );
};

export default ProcessItem;
